// Unpublished Work © 2022-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {connect} from 'react-redux';
import {withRouter} from 'react-router-dom';
import EquipmentName from 'OnEquip/equipment/common/data-table/equipment-name';
import {fetchEffectData} from 'Utils/react-utils';
import {alphaNumericCompare} from 'Ui/models/maintenance';
import {useEquipmentContext} from 'OnEquip/equipment/context/equipment-context';
import {MAINTENANCE} from 'OnEquip/equipment/constants/equipment-details-tabs';
import {ASSIGNMENTS} from 'Common/constants/routes';
import {getDeploymentsInUse} from 'Services/fleet-service';
import moment from 'moment';
import {DatePicker} from '@deere/isg.component-library';
import {getHoc} from 'Services/hoc-service';
import {formatArrayToStringOrDefault} from 'OnEquip/equipment/utils/equipment-detail-formatters';
import {getHocValue} from 'Utils/unit-conversion-utils';
import {VIEW_WORKBOARDS} from 'Common/constants/business-activities';
import DataGridWrapper from 'Src/ui-core/components/common/data-table/data-grid-wrapper';

function getDataGridColumns(translations) {
    return [
        {
            headerName: translations.EQUIPMENT_LIST,
            field: 'equipmentName',
            flex: 1,
            minWidth: 150,
            sortComparator: alphaNumericCompare,
            renderCell: (params) => {
                return (
                    <EquipmentName
                        equipment={params.row}
                        tab={MAINTENANCE}
                        type={ASSIGNMENTS}
                    />
                );
            }
        },
        {
            headerName: translations.MODEL,
            flex: 1,
            minWidth: 150,
            field: 'modelAndManufacturerName',
            sortComparator: alphaNumericCompare
        },
        {
            headerName: translations.OPERATOR,
            flex: 1,
            minWidth: 150,
            field: 'operator',
            sortComparator: alphaNumericCompare,
            requiredMyJdPermissions: VIEW_WORKBOARDS
        },
        {
            headerName: translations.jobs_job,
            flex: 1,
            minWidth: 150,
            field: 'job',
            sortComparator: alphaNumericCompare
        },
        {
            headerName: translations.AREA,
            flex: 1,
            minWidth: 150,
            className: 'equipment-area',
            field: 'area',
            sortComparator: alphaNumericCompare
        },
        {
            headerName: translations.ONLINK_HOC,
            flex: 1,
            minWidth: 150,
            className: 'hoc',
            field: 'hoc',
            sortComparator: alphaNumericCompare
        }
    ];
}

function getFilterComponent(date, setDate) {
    return (
        <div className='extra-table-options'>
            <DatePicker
                onChange={(date) => setDate(moment(date))}
                value={date.toDate()}
            />
        </div>
    );
}

function initializeState() {
    const [assignments, setAssignments] = React.useState(() => []);
    const [date, setDate] = React.useState(() => moment());
    const [loading, setLoading] = React.useState(() => true);

    return {
        assignments,
        setAssignments,
        date,
        setDate,
        loading,
        setLoading
    };
}

function Assignments(props) {
    const {
        fleetId,
        translations
    } = props;

    const {
        assignments,
        setAssignments,
        date,
        setDate,
        loading,
        setLoading
    } = initializeState();

    const equipmentContext = useEquipmentContext();

    equipmentContext.current = async (isMounted = () => true) => {
        setLoading(true);

        let formattedAssignments = [];

        if (date?.isValid()) {
            const formattedDate = date.format('YYYYMMDD');

            const [
                {deployments},
                {equipmentAreas}
            ] = await Promise.all([
                getDeploymentsInUse(fleetId, formattedDate),
                getHoc(formattedDate)
            ]);

            formattedAssignments = deployments.map((deployment) => {
                const {
                    equipment,
                    firstName,
                    lastName,
                    jobName,
                    equipmentAreaId,
                    workItemId
                } = deployment;

                const {
                    equipmentName,
                    equipmentId,
                    manufacturerName,
                    modelName
                } = equipment || {};

                const hocEquipmentArea = equipmentAreas.find((equipArea) => equipArea.equipmentAreaId === equipmentAreaId);

                return {
                    equipmentId: equipmentId || '',
                    equipmentName: equipmentName || '',
                    modelAndManufacturerName: formatArrayToStringOrDefault([manufacturerName, modelName]),
                    operator: formatArrayToStringOrDefault([firstName, lastName]),
                    job: jobName || '',
                    area: hocEquipmentArea?.name || '',
                    hoc: getHocValue(hocEquipmentArea, translations),
                    workItemId: workItemId || ''
                };
            });
        }

        if (isMounted()) {
            setAssignments(formattedAssignments);
            setLoading(false);
        }
    };

    React.useEffect(() => fetchEffectData(equipmentContext.current), [fleetId, date]);

    return (
        <DataGridWrapper
            columns={getDataGridColumns(translations)}
            filterComponent={getFilterComponent(date, setDate)}
            getRowId={(row) => row.workItemId}
            initialState={{
                sorting: {
                    sortModel: [{
                        field: 'equipmentName',
                        sort: 'asc'
                    }]
                }
            }}
            loading={loading}
            noDataAvailableMessage={translations.ONLINK_NO_ASSIGNMENTS_FOR_DATE_SELECTED}
            rows={assignments}
            searchFields={['equipmentName', 'modelAndManufacturerName', 'operator', 'job', 'area', 'hoc']}
            searchable={true}
            slotProps={{
                root: {
                    disableColumnMenu: true
                }
            }}
            translations={translations}
        />
    );
}

Assignments.propTypes = {
    addToast: PropTypes.func,
    fleetId: PropTypes.string,
    location: PropTypes.location,
    openConfirmation: PropTypes.func,
    translations: PropTypes.translations
};

export function mapStateToProps(state) {
    return {
        fleetId: state.membership.fleetId,
        translations: state.translations
    };
}

export default connect(mapStateToProps)(withRouter(Assignments));
